import {useEffect, useMemo, useState} from "react"
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateProfile } from "../../../api/requests/auth-requests";
import { toast } from "react-toastify";

export const useProfileUpdate = (me, onUpdated) => {
    const [loading, setLoading] = useState(false);

    // Şifre görünürlük state'leri
    const [showOld, setShowOld] = useState(false); // Yeni eklendi
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const initialValues = useMemo(() => ({
        phoneNumber: me?.phoneNumber || "",
        username: me?.username || "",
        email: me?.email || "",
        oldPassword: "", // Yeni eklendi
        newPassword: "",
        confirmNewPassword: "",
        twoFaEnabled: me?.twoFaEnabled || false,
    }), [me]);

    const validationSchema = Yup.object({
        phoneNumber: Yup.string()
            .test(
                "len",
                "Telefon numarası eksik (Başına 0 koymadan 10 hane giriniz)",
                (val) => {
                    if (!val) return false;
                    return val.length === 13;
                }
            )
            .max(20, "Telefon numarası çok uzun"),
        username: Yup.string()
            .min(3, "Kullanıcı adı en az 3 karakter olmalı")
            .max(50, "Kullanıcı adı en fazla 50 karakter olabilir")
            .nullable(),
        email: Yup.string()
            .email("Geçerli bir e-posta giriniz")
            .max(100, "E-posta en fazla 100 karakter olabilir")
            .nullable(),

        // Yeni Şifre kuralları
        newPassword: Yup.string()
            .min(6, "Yeni şifre en az 6 karakter olmalı")
            .nullable(),

        // Eski Şifre: Eğer yeni şifre girilmişse, eski şifre zorunludur.
        oldPassword: Yup.string().when("newPassword", {
            is: (val) => val && val.length > 0,
            then: (schema) => schema.required("Şifre değiştirmek için mevcut şifrenizi girmelisiniz"),
            otherwise: (schema) => schema.nullable(),
        }),

        confirmNewPassword: Yup.string()
            .oneOf([Yup.ref("newPassword"), null], "Şifreler uyuşmuyor")
            .nullable(),
        twoFaEnabled: Yup.boolean().nullable(),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {};

                if (values.phoneNumber !== (me?.phoneNumber || "")) payload.phoneNumber = values.phoneNumber;
                if (values.username !== (me?.username || "")) payload.username = values.username;
                if (values.email !== (me?.email || "")) payload.email = values.email;
                if (values.twoFaEnabled !== (me?.twoFaEnabled || false)) payload.twoFaEnabled = values.twoFaEnabled;

                // Şifre değişimi varsa
                if (values.newPassword) {
                    payload.newPassword = values.newPassword;
                    payload.oldPassword = values.oldPassword; // Artık gönderiliyor
                }

                if (Object.keys(payload).length === 0) {
                    toast.info("Değişiklik yapmadınız.");
                    setLoading(false);
                    return;
                }

                const res = await updateProfile(payload);

                const json = await res.json();

                if (!res.ok) {
                    toast.error(json.message || "Profil güncellenemedi");
                    setLoading(false);
                    return;
                }

                toast.success(json.message || "Profil güncellendi");

                // Başarılı olursa şifre alanlarını temizle
                formik.resetForm({
                    values: {
                        ...values,
                        oldPassword: "",
                        newPassword: "",
                        confirmNewPassword: "",
                    }
                });

                onUpdated && onUpdated();
            } catch (err) {
                console.error(err);
                toast.error("Sunucu hatası");
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (formik.submitCount > 0 && !formik.isValid) {
            const errorMessages = Object.values(formik.errors);
            if (errorMessages.length > 0) {
                toast.error(errorMessages[0]);
            }
        }
    }, [formik.submitCount]);

    return {
        formik,
        loading,
        showOld, setShowOld,
        showNew, setShowNew,
        showConfirm, setShowConfirm,
    };
};